import React from 'react';
import { Link } from 'react-router-dom';
import { ChevronUp, ChevronDown, Clock, Loader2 } from 'lucide-react';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { ProductThumbnail } from '@/components/products/ProductThumbnail';
import { formatSelectedVariant, variantKey } from '@/lib/variant';

const ORANGE = '#FF6000';

export function CheckoutLegalConsent({
  t,
  privacyPolicyAccepted,
  setPrivacyPolicyAccepted,
  checkboxId = 'privacy-policy-summary',
  compact = false,
}) {
  return (
    <div className={compact ? '' : 'px-4 pb-4 pt-1 border-t'}>
      <div className="flex items-start gap-2">
        <Checkbox
          id={checkboxId}
          checked={privacyPolicyAccepted}
          onCheckedChange={setPrivacyPolicyAccepted}
          className="mt-0.5 h-4 w-4 shrink-0"
          data-testid="privacy-policy-checkbox"
        />
        <Label
          htmlFor={checkboxId}
          className={`${compact ? 'text-[10px] max-h-16 overflow-y-auto' : 'text-[11px]'} text-gray-500 leading-relaxed cursor-pointer`}
        >
          {t('checkout.legalConsentText')}{' '}
          <Link to="/terms" target="_blank" className="font-semibold underline hover:text-[#FF6000]">
            {t('checkout.termsAndConditions')}
          </Link>{' '}
          {t('checkout.and')}{' '}
          <Link
            to="/refund-policy"
            target="_blank"
            className="font-semibold underline hover:text-[#FF6000]"
          >
            {t('checkout.cancellationPolicy')}
          </Link>
          {t('checkout.legalConsentText2')}{' '}
          <Link
            to="/privacy-policy"
            target="_blank"
            className="font-semibold underline hover:text-[#FF6000]"
          >
            {t('checkout.readPrivacyPolicy')}
          </Link>
        </Label>
      </div>
      {!privacyPolicyAccepted && (
        <p className={`${compact ? 'text-[10px] ml-6' : 'text-[11px] ml-6'} text-red-500 mt-1 font-medium`}>
          {t('checkout.privacyPolicyRequired')}
        </p>
      )}
    </div>
  );
}

export function CheckoutSummaryBreakdown({
  t,
  formatPrice,
  currency,
  items,
  getLocalizedProductName,
  productLangKey,
  savingsInfo,
  savingsOpen,
  setSavingsOpen,
  shippingConverted,
  shippingDestination,
  tax,
  totalWeightKg,
  privacyPolicyAccepted,
  setPrivacyPolicyAccepted,
  showItems = true,
  showLegal = true,
  checkboxId = 'privacy-policy-summary',
  onClose,
}) {
  const hasSavings = savingsInfo.totalSaving > 0.009;

  return (
    <div className="bg-white">
      {onClose && (
        <div className="relative flex items-center justify-end px-2 pt-1.5 pb-0.5">
          <div className="absolute left-1/2 -translate-x-1/2 w-10 h-1 rounded-full bg-gray-300" />
          <button
            type="button"
            onClick={onClose}
            className="relative z-10 p-2 text-gray-600 hover:text-gray-900"
            aria-label={t('common.close', 'Kapat')}
            data-testid="checkout-summary-close"
          >
            <ChevronDown className="w-6 h-6" />
          </button>
        </div>
      )}
      {showItems && items.length > 0 && (
        <div className="flex gap-2 overflow-x-auto px-4 pt-4 pb-2 scrollbar-none">
          {items.map((item) => {
            const name =
              getLocalizedProductName?.(item.product, productLangKey) ||
              item.product?.name ||
              '';
            const variantLabel = formatSelectedVariant(item.selected_variant);
            return (
              <div
                key={`${item.product_id}-${variantKey(item.selected_variant)}`}
                className="relative w-12 h-12 rounded-md overflow-hidden bg-gray-100 shrink-0 border border-gray-100"
                title={variantLabel ? `${name} (${variantLabel})` : name}
              >
                <ProductThumbnail
                  src={item.product?.images?.[0]}
                  alt={name}
                  className="h-full w-full"
                />
                {item.quantity > 1 && (
                  <span className="absolute top-0 left-0 text-[9px] font-bold text-white bg-[#FF6000] px-1 rounded-br">
                    x{item.quantity}
                  </span>
                )}
              </div>
            );
          })}
        </div>
      )}

      <div className="px-4 py-3 flex justify-between items-center text-sm">
        <span className="font-semibold text-gray-900">{t('checkout.products')}</span>
        <span className="font-bold text-gray-900">
          {formatPrice(savingsInfo.productsOriginal, currency)}
        </span>
      </div>

      {hasSavings && (
        <div className="mx-3 mb-3 rounded-lg overflow-hidden">
          <button
            type="button"
            onClick={() => setSavingsOpen((v) => !v)}
            className="w-full flex items-center justify-between px-3 py-2.5 bg-[#E8F5E9] text-left"
          >
            <span className="flex items-center gap-1 text-sm font-semibold text-[#1B8A3E]">
              {t('checkout.yourSavings')}
              {savingsOpen ? (
                <ChevronUp className="w-4 h-4" />
              ) : (
                <ChevronDown className="w-4 h-4" />
              )}
            </span>
            <span className="text-sm font-bold text-[#1B8A3E]">
              -{formatPrice(savingsInfo.totalSaving, currency)}
            </span>
          </button>
          {savingsOpen && (
            <div className="bg-[#F3FAF4] px-3 py-2 space-y-1.5">
              {savingsInfo.lines.map((line) => (
                <div
                  key={line.key}
                  className="flex justify-between text-xs text-gray-600"
                >
                  <span>{line.label}</span>
                  <span>-{formatPrice(line.amount, currency)}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      <div className="px-4 pb-3 space-y-2 text-sm">
        <div className="flex justify-between">
          <span className="text-gray-500">
            {t('cart.shipping')}
            {totalWeightKg > 0 ? ` (${totalWeightKg.toFixed(2)} kg)` : ''}
          </span>
          <span className={shippingConverted === 0 ? 'text-[#1B8A3E] font-medium' : 'text-gray-900'}>
            {shippingConverted === 0
              ? t('cart.free')
              : formatPrice(shippingConverted, currency)}
          </span>
        </div>
        <p className="text-[11px] text-gray-400 -mt-1">
          {shippingDestination === 'TR'
            ? t('cart.shippingDomestic')
            : t('cart.shippingInternational')}
        </p>
        {totalWeightKg > 10 && (
          <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-2 py-1.5">
            {t('checkout.weightOver10Warning')}
          </p>
        )}
        <div className="flex justify-between">
          <span className="text-gray-500">
            {t('cart.tax')}
            <span className="text-[11px] text-gray-400 ml-1">
              ({t('cart.taxByProductRate')})
            </span>
          </span>
          <span className="text-gray-900">{formatPrice(tax, currency)}</span>
        </div>
      </div>

      {showLegal && (
        <CheckoutLegalConsent
          t={t}
          privacyPolicyAccepted={privacyPolicyAccepted}
          setPrivacyPolicyAccepted={setPrivacyPolicyAccepted}
          checkboxId={checkboxId}
        />
      )}
    </div>
  );
}

export function CheckoutStickyBar({
  t,
  formatPrice,
  currency,
  total,
  savingsInfo,
  mobileSummaryOpen,
  setMobileSummaryOpen,
  onConfirm,
  confirmLabel,
  loading,
  disabled,
  showOnDesktop = false,
}) {
  return (
    <div
      className={`${showOnDesktop ? 'flex' : 'flex lg:hidden'} fixed bottom-0 left-0 right-0 z-50 bg-white border-t shadow-[0_-4px_16px_rgba(0,0,0,0.08)]`}
      style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
    >
      <div className="w-full max-w-6xl mx-auto flex items-center gap-3 px-3 py-2.5">
        <button
          type="button"
          onClick={() => setMobileSummaryOpen((v) => !v)}
          className="flex-1 min-w-0 text-left"
        >
          <p className="text-[10px] font-semibold tracking-wide" style={{ color: ORANGE }}>
            {t('cart.total').toUpperCase()}
          </p>
          <p className="text-lg font-bold text-gray-900 leading-tight flex items-center gap-1">
            {formatPrice(total, currency)}
            <ChevronDown
              className={`w-4 h-4 text-[#FF6000] transition-transform ${mobileSummaryOpen ? 'rotate-180' : ''}`}
            />
          </p>
          {savingsInfo.totalSaving > 0.009 && (
            <p className="text-[11px] text-[#1B8A3E] font-medium truncate">
              {formatPrice(savingsInfo.totalSaving, currency)} {t('checkout.yourSavings').toLowerCase()}
            </p>
          )}
        </button>
        <button
          type="button"
          onClick={onConfirm}
          disabled={disabled}
          data-testid="place-order-btn"
          className="shrink-0 rounded-lg text-white font-semibold px-4 py-2.5 min-w-[148px] disabled:opacity-50 disabled:cursor-not-allowed"
          style={{ backgroundColor: disabled ? '#f4a574' : ORANGE }}
        >
          {loading ? (
            <span className="inline-flex items-center justify-center">
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              {t('checkout.processing')}
            </span>
          ) : (
            <span className="flex flex-col items-center leading-tight">
              <span className="text-sm">{confirmLabel}</span>
              {savingsInfo.lowStockCount > 0 && (
                <span className="text-[10px] font-normal opacity-90 inline-flex items-center gap-1 mt-0.5">
                  <Clock className="w-3 h-3" />
                  {t('checkout.stockRunningOut', { count: savingsInfo.lowStockCount })}
                </span>
              )}
            </span>
          )}
        </button>
      </div>
    </div>
  );
}

export function CheckoutDesktopSidebar({
  t,
  confirmLabel,
  onConfirm,
  loading,
  disabled,
  savingsInfo,
  ...breakdownProps
}) {
  return (
    <div className="hidden lg:block">
      <div className="bg-white rounded-xl overflow-hidden shadow-sm border border-gray-100 sticky top-24">
        <CheckoutSummaryBreakdown
          t={t}
          savingsInfo={savingsInfo}
          {...breakdownProps}
        />
        <div className="px-4 pb-4">
          <button
            type="button"
            onClick={onConfirm}
            disabled={disabled}
            data-testid="place-order-btn-desktop"
            className="w-full rounded-lg text-white font-semibold py-3 disabled:opacity-50 disabled:cursor-not-allowed"
            style={{ backgroundColor: disabled ? '#f4a574' : ORANGE }}
          >
            {loading ? (
              <span className="inline-flex items-center justify-center">
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                {t('checkout.processing')}
              </span>
            ) : (
              <span className="flex flex-col items-center leading-tight">
                <span>{confirmLabel}</span>
                {savingsInfo.lowStockCount > 0 && (
                  <span className="text-[11px] font-normal opacity-90 inline-flex items-center gap-1 mt-0.5">
                    <Clock className="w-3 h-3" />
                    {t('checkout.stockRunningOut', { count: savingsInfo.lowStockCount })}
                  </span>
                )}
              </span>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

export function OrangeRadio({ selected }) {
  return (
    <span
      className={`inline-flex w-5 h-5 rounded-full border-2 items-center justify-center shrink-0 ${
        selected ? 'border-[#FF6000]' : 'border-gray-300'
      }`}
    >
      {selected ? <span className="w-2.5 h-2.5 rounded-full bg-[#FF6000]" /> : null}
    </span>
  );
}

export function CheckoutSectionBand({ children }) {
  return (
    <div className="bg-[#EEEEEE] text-[11px] font-semibold tracking-[0.08em] text-gray-500 uppercase px-4 py-2">
      {children}
    </div>
  );
}
